import React, { useState, useCallback } from 'react';
import { useAdminAuth } from '../auth/useAdminAuth';
import './DocumentManagement.css';

const SUBJECT_OPTIONS = ['math', 'chinese', 'science', 'english', 'other'];
const DOC_TYPE_OPTIONS = ['worksheet', 'syllabus', 'textbook', 'notes', 'exam', 'other'];
const PIPELINE_STATUS_OPTIONS = ['pending', 'extracting', 'classifying', 'completed', 'failed'];
const PAGE_LIMIT = 20;

const getClient = async () => {
  const { generateClient } = await import('aws-amplify/data');
  return generateClient();
};

const DocumentManagement = () => {
  const { user } = useAdminAuth();

  // Search & filter state
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    subject: '',
    documentType: '',
    pipelineStatus: '',
  });

  // Data state
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Detail view state
  const [selectedDoc, setSelectedDoc] = useState(null);

  const fetchDocuments = useCallback(async (token = null) => {
    setLoading(true);
    setError(null);

    try {
      const client = await getClient();
      const activeFilters = {};
      if (filters.subject) activeFilters.subject = filters.subject;
      if (filters.documentType) activeFilters.documentType = filters.documentType;
      if (filters.pipelineStatus) activeFilters.pipelineStatus = filters.pipelineStatus;

      const result = await client.graphql({
        query: `query AdminSearchDocuments($searchText: String, $filters: AWSJSON, $limit: Int, $nextToken: String) {
          adminSearchDocuments(searchText: $searchText, filters: $filters, limit: $limit, nextToken: $nextToken)
        }`,
        variables: {
          searchText: searchText || null,
          filters: Object.keys(activeFilters).length > 0 ? JSON.stringify(activeFilters) : null,
          limit: PAGE_LIMIT,
          nextToken: token,
        },
      });

      const data = JSON.parse(result.data.adminSearchDocuments);
      if (token) {
        setDocuments((prev) => [...prev, ...(data.items || [])]);
      } else {
        setDocuments(data.items || []);
      }
      setNextToken(data.nextToken || null);
      setHasSearched(true);
    } catch (err) {
      console.error('[DocumentManagement] Search error:', err);
      setError(err.message || 'Failed to search documents');
    } finally {
      setLoading(false);
    }
  }, [searchText, filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    setDocuments([]);
    setNextToken(null);
    fetchDocuments(null);
  };

  const handleLoadMore = () => {
    if (nextToken) {
      fetchDocuments(nextToken);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setSearchText('');
    setFilters({ subject: '', documentType: '', pipelineStatus: '' });
    setDocuments([]);
    setNextToken(null);
    setHasSearched(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'doc-mgmt__badge--success';
      case 'failed': return 'doc-mgmt__badge--error';
      case 'pending': return 'doc-mgmt__badge--warning';
      case 'extracting':
      case 'classifying': return 'doc-mgmt__badge--info';
      default: return '';
    }
  };

  // Detail view
  if (selectedDoc) {
    return (
      <div className="doc-mgmt">
        <div className="doc-mgmt__detail">
          <button
            className="doc-mgmt__back-btn"
            onClick={() => setSelectedDoc(null)}
          >
            ← Back to list
          </button>
          <div className="doc-mgmt__detail-header">
            <h1 className="doc-mgmt__title">
              {selectedDoc.title || selectedDoc.fileName || 'Untitled Document'}
            </h1>
            <span className="doc-mgmt__badge doc-mgmt__badge--readonly">View Only</span>
          </div>

          <div className="doc-mgmt__detail-grid">
            <div className="doc-mgmt__detail-field">
              <span className="doc-mgmt__detail-label">File Name</span>
              <span className="doc-mgmt__detail-value">{selectedDoc.fileName || '—'}</span>
            </div>
            <div className="doc-mgmt__detail-field">
              <span className="doc-mgmt__detail-label">User ID</span>
              <span className="doc-mgmt__detail-value doc-mgmt__detail-value--mono">{selectedDoc.userId || '—'}</span>
            </div>
            <div className="doc-mgmt__detail-field">
              <span className="doc-mgmt__detail-label">Subject</span>
              <span className="doc-mgmt__detail-value">{selectedDoc.subject || '—'}</span>
            </div>
            <div className="doc-mgmt__detail-field">
              <span className="doc-mgmt__detail-label">Document Type</span>
              <span className="doc-mgmt__detail-value">{selectedDoc.documentType || '—'}</span>
            </div>
            <div className="doc-mgmt__detail-field">
              <span className="doc-mgmt__detail-label">Pipeline Status</span>
              <span className={`doc-mgmt__badge ${getStatusClass(selectedDoc.pipelineStatus)}`}>
                {selectedDoc.pipelineStatus || '—'}
              </span>
            </div>
            <div className="doc-mgmt__detail-field">
              <span className="doc-mgmt__detail-label">Uploaded At</span>
              <span className="doc-mgmt__detail-value">{formatDate(selectedDoc.uploadedAt)}</span>
            </div>
            {selectedDoc.gradeLevel && (
              <div className="doc-mgmt__detail-field">
                <span className="doc-mgmt__detail-label">Grade Level</span>
                <span className="doc-mgmt__detail-value">{selectedDoc.gradeLevel}</span>
              </div>
            )}
            {selectedDoc.difficulty && (
              <div className="doc-mgmt__detail-field">
                <span className="doc-mgmt__detail-label">Difficulty</span>
                <span className="doc-mgmt__detail-value">{selectedDoc.difficulty}</span>
              </div>
            )}
          </div>

          {selectedDoc.summary && (
            <div className="doc-mgmt__detail-section">
              <h3 className="doc-mgmt__detail-section-title">Summary</h3>
              <p className="doc-mgmt__detail-text">{selectedDoc.summary}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="doc-mgmt">
      <div className="doc-mgmt__header">
        <h1 className="doc-mgmt__title">Document Management</h1>
        <p className="doc-mgmt__description">
          Search and browse all user documents across the platform
        </p>
      </div>

      {/* Search and filters */}
      <form className="doc-mgmt__controls" onSubmit={handleSearch}>
        <div className="doc-mgmt__search">
          <input
            type="text"
            className="doc-mgmt__search-input"
            placeholder="Search by filename or title..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <div className="doc-mgmt__filters">
          <select
            className="doc-mgmt__filter-select"
            value={filters.subject}
            onChange={(e) => handleFilterChange('subject', e.target.value)}
            aria-label="Filter by subject"
          >
            <option value="">All Subjects</option>
            {SUBJECT_OPTIONS.map((s) => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>

          <select
            className="doc-mgmt__filter-select"
            value={filters.documentType}
            onChange={(e) => handleFilterChange('documentType', e.target.value)}
            aria-label="Filter by document type"
          >
            <option value="">All Types</option>
            {DOC_TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
            ))}
          </select>

          <select
            className="doc-mgmt__filter-select"
            value={filters.pipelineStatus}
            onChange={(e) => handleFilterChange('pipelineStatus', e.target.value)}
            aria-label="Filter by pipeline status"
          >
            <option value="">All Statuses</option>
            {PIPELINE_STATUS_OPTIONS.map((p) => (
              <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
            ))}
          </select>
        </div>

        <div className="doc-mgmt__actions">
          <button type="submit" className="doc-mgmt__search-btn" disabled={loading}>
            {loading ? 'Searching...' : '🔍 Search'}
          </button>
          <button
            type="button"
            className="doc-mgmt__clear-btn"
            onClick={handleClearFilters}
          >
            Clear
          </button>
        </div>
      </form>

      {/* Error */}
      {error && (
        <div className="doc-mgmt__error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Results */}
      <div className="doc-mgmt__results">
        {loading && documents.length === 0 ? (
          <div className="doc-mgmt__loading">Loading documents...</div>
        ) : documents.length > 0 ? (
          <>
            <div className="doc-mgmt__list">
              {documents.map((doc, idx) => (
                <div
                  key={doc.id || idx}
                  className="doc-mgmt__card"
                  onClick={() => setSelectedDoc(doc)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSelectedDoc(doc); }}
                >
                  <div className="doc-mgmt__card-header">
                    <h3 className="doc-mgmt__card-title">
                      {doc.title || doc.fileName || 'Untitled'}
                    </h3>
                    <span className={`doc-mgmt__badge ${getStatusClass(doc.pipelineStatus)}`}>
                      {doc.pipelineStatus || 'unknown'}
                    </span>
                  </div>
                  <div className="doc-mgmt__card-meta">
                    {doc.subject && (
                      <span className="doc-mgmt__badge doc-mgmt__badge--subject">
                        {doc.subject}
                      </span>
                    )}
                    {doc.documentType && (
                      <span className="doc-mgmt__badge doc-mgmt__badge--type">
                        {doc.documentType}
                      </span>
                    )}
                  </div>
                  <div className="doc-mgmt__card-footer">
                    <span className="doc-mgmt__card-user">
                      User: <span className="doc-mgmt__card-user-id">{doc.userId || '—'}</span>
                    </span>
                    <span className="doc-mgmt__card-date">{formatDate(doc.uploadedAt)}</span>
                  </div>
                </div>
              ))}
            </div>

            {nextToken && (
              <div className="doc-mgmt__pagination">
                <button
                  className="doc-mgmt__load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        ) : hasSearched ? (
          <div className="doc-mgmt__empty">
            <p>No documents found</p>
            <p className="doc-mgmt__empty-hint">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="doc-mgmt__empty">
            <p>Use the search and filters above to find documents</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentManagement;
